import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import TempoKeyChip from "@/components/tempo/TempoKeyChip";
import { INSTRUMENTAL, channelOf } from "@/components/recstudio/sessionEngine";
import { analyzeTempoKey } from "@/lib/tempoKey";
import { saveSongTempo } from "@/lib/mixDeskApi";

/** Tempo + key of the session's music — found once the audio loads, correctable, saved with the session (and its song). */
export default function InstrumentalTempo({ session, buffer }) {
  const { doc, engine, change } = session;
  const inst = doc.instrumental;
  const [detecting, setDetecting] = useState(false);
  const tried = useRef(null);
  const songSave = useRef(null);
  const pendingSong = useRef(null);
  const idx = channelOf(engine, doc.id, INSTRUMENTAL);
  const fresh = Boolean(buffer) && idx != null && engine.channels[idx].recKey === inst.url && engine.channels[idx].buffer === buffer;
  const put = useCallback((url, tempo) => change((d) => (d.instrumental?.url === url ? { ...d, instrumental: { ...d.instrumental, tempo } } : d)), [change]);
  const detect = useCallback(async () => {
    const { url, song_id: songId } = inst;
    setDetecting(true);
    await new Promise((r) => setTimeout(r, 40));
    try {
      const t = await analyzeTempoKey(buffer);
      put(url, t);
      if (songId) saveSongTempo(songId, t).catch(() => {});
    } catch { toast.error("Couldn't find the tempo and key", { description: "Press Fix to set them yourself." }); }
    finally { setDetecting(false); }
  }, [buffer, inst, put]);
  useEffect(() => {
    if (fresh && !inst.tempo && tried.current !== buffer) { tried.current = buffer; detect(); }
  }, [fresh, inst.tempo, buffer, detect]);
  const flushSong = useCallback(() => {
    clearTimeout(songSave.current);
    const p = pendingSong.current;
    pendingSong.current = null;
    if (p) saveSongTempo(p.id, p.tempo).catch(() => {});
  }, []);
  useEffect(() => flushSong, [flushSong]);
  const onChange = (tempo) => {
    put(inst.url, tempo);
    if (!inst.song_id) return;
    pendingSong.current = { id: inst.song_id, tempo };
    clearTimeout(songSave.current);
    songSave.current = setTimeout(flushSong, 700);
  };
  const songTime = () => (engine.playing ? engine.getPlayhead() - ((engine.ctx.outputLatency || 0) + (engine.ctx.baseLatency || 0)) : null);
  return (
    <div data-testid="rec-instrumental-tempo" className="mt-2">
      <TempoKeyChip tempo={inst.tempo} detecting={detecting} onChange={onChange} onRedetect={fresh ? detect : null} getSongTime={songTime} getCtx={() => engine.ctx} />
    </div>
  );
}
